import React, { useState, useEffect } from 'react';
import { Button, Table, Badge, Spinner, Alert, Card, Modal } from 'react-bootstrap';
import { workoutAPI } from '../services/workoutAPI';
import ProgramForm from './ProgramForm';
import StartProgramModal from './StartProgramModal';

function ProgramManager({ onBack }) {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [startingProgram, setStartingProgram] = useState(null);
  const [showStartProgramModal, setShowStartProgramModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await workoutAPI.fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingProgram(null);
    setShowForm(true);
  };

  const handleEdit = async (program) => {
    try {
      // Fetch full program details including workouts
      const fullProgram = await workoutAPI.fetchProgramById(program.id);
      setEditingProgram(fullProgram);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching program details:', err);
      setError('Failed to load program details. Please try again.');
    }
  };

  const handleSave = async (formData, autoSavedProgramId = null) => {
    setError(null);
    try {
      if (editingProgram) {
        await workoutAPI.updateProgram(editingProgram.id, formData);
      } else if (autoSavedProgramId) {
        // Program was auto-saved when creating inline templates, just update it
        await workoutAPI.updateProgram(autoSavedProgramId, formData);
      } else {
        await workoutAPI.createProgram(formData);
      }
      setShowForm(false);
      setEditingProgram(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error saving program:', err);
      setError('Failed to save program. Please try again.');
    }
  };

  const handleDeleteClick = (program) => {
    setProgramToDelete(program);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete) return;

    setError(null);
    try {
      await workoutAPI.deleteProgram(programToDelete.id);
      setShowDeleteModal(false);
      setProgramToDelete(null);
      await fetchPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      setError('Failed to delete program. Please try again.');
      setShowDeleteModal(false);
      setProgramToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProgramToDelete(null);
  };

  const handleStartProgram = (program) => {
    // Show modal to choose start date
    setStartingProgram(program);
    setShowStartProgramModal(true);
  };

  const handleStartProgramSuccess = async () => {
    setSuccessMessage(`Program "${startingProgram.name}" started successfully! Go to the landing page to continue.`);
    setShowStartProgramModal(false);
    setStartingProgram(null);
    await fetchPrograms();
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading programs...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading programs...</p>
      </div>
    );
  }

  return (
    <div className="program-manager">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>
            <i className="fas fa-calendar-week me-2"></i>
            Workout Programs
          </h2>
          <p className="text-muted">Create and manage your multi-week training programs</p>
        </div>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </Alert>
      )}

      <div className="mb-4">
        <Button variant="primary" onClick={handleCreateNew}>
          <i className="fas fa-plus me-2"></i>
          Create New Program
        </Button>
      </div>

{(() => {
        // Separate programs into templates and started copies
        const templatePrograms = programs.filter(p => p.is_template === 1 || p.is_template === true);
        const startedPrograms = programs.filter(p => p.is_template === 0 || p.is_template === false);

        if (programs.length === 0) {
          return (
            <Card>
              <Card.Body className="text-center py-5">
                <i className="fas fa-calendar-week fa-4x text-muted mb-3"></i>
                <h4>No Programs Yet</h4>
                <p className="text-muted">
                  Create your first workout program to organize your training into a structured plan
                </p>
                <Button variant="primary" onClick={handleCreateNew}>
                  <i className="fas fa-plus me-2"></i>
                  Create Program
                </Button>
              </Card.Body>
            </Card>
          );
        }

        const renderProgramTable = (programsList, showStartButton = true) => (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Workouts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programsList.map((program) => (
                <tr key={program.id}>
                  <td>
                    <strong>{program.name}</strong>
                  </td>
                  <td>
                    <span className="text-muted">
                      {program.description || 'No description'}
                    </span>
                  </td>
                  <td>
                    <Badge bg="info">
                      {program.workout_count || 0} workout{(program.workout_count || 0) !== 1 ? 's' : ''}
                    </Badge>
                  </td>
                  <td>
                    <div className="btn-group" role="group">
                      {showStartButton && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleStartProgram(program)}
                          disabled={startingProgram === program.id}
                          title="Creates your own copy of this program and activates it so you can start tracking workouts"
                        >
                          {startingProgram === program.id ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-1"
                              />
                              Starting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-play me-1"></i>
                              Start
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(program)}
                        title="Edit program"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(program)}
                        title="Delete program"
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        );

        return (
          <div className="programs-list">
            {/* Template Programs Section */}
            {templatePrograms.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-file-alt me-2"></i>
                    Program Templates
                    <Badge bg="secondary" className="ms-2">{templatePrograms.length}</Badge>
                  </h5>
                  <small className="text-muted">Your program templates - click "Start" to begin tracking</small>
                </Card.Header>
                <Card.Body className="p-0">
                  {renderProgramTable(templatePrograms, true)}
                </Card.Body>
              </Card>
            )}

            {/* Started Programs Section */}
            {startedPrograms.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-running me-2"></i>
                    Started Programs
                    <Badge bg="success" className="ms-2">{startedPrograms.length}</Badge>
                  </h5>
                  <small className="text-muted">Programs you've started - these are your active tracking copies</small>
                </Card.Header>
                <Card.Body className="p-0">
                  {renderProgramTable(startedPrograms, false)}
                </Card.Body>
              </Card>
            )}
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-danger me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Are you sure you want to delete the program <strong>"{programToDelete?.name}"</strong>?
          </p>
          <p className="text-muted mt-2 mb-0">
            This action cannot be undone. All workouts in this program will be removed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <i className="fas fa-trash me-2"></i>
            Delete Program
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Start Program Modal */}
      {showStartProgramModal && startingProgram && (
        <StartProgramModal
          program={startingProgram}
          onClose={() => {
            setShowStartProgramModal(false);
            setStartingProgram(null);
          }}
          onSuccess={handleStartProgramSuccess}
        />
      )}

      {showForm && (
        <ProgramForm
          show={showForm}
          onHide={() => {
            setShowForm(false);
            setEditingProgram(null);
            setError(null);
          }}
          onSave={handleSave}
          program={editingProgram}
          error={error}
        />
      )}
    </div>
  );
}

export default ProgramManager;
